import { Planet } from '@apeiron/planet'
import { CustomError, deserializeData } from '@apeiron/temp'
import { useLazyQuery } from '@apollo/client'
import { ERROR_CODE } from '@src/constants/error'
import { RETRY } from '@src/constants/retry'
import { planetSchema } from '@src/deserialize/yup/planet'
import graphql from '@src/graphql/query/verifyPlanet'
import pRetry from 'p-retry'
import * as R from 'ramda'
import { useCallback } from 'react'

const useVerifyPlanet = (): Hook => {
  const [query] = useLazyQuery(graphql)

  const doVerify = useCallback(
    async (
      planetID: string,
      passCondition?: (planet: Planet) => boolean
    ): Promise<boolean> => {
      const response = await query({ variables: { planetID } })

      const planet = deserializeData<Planet>(response, planetSchema)

      const pass =
        planet && passCondition ? passCondition(planet) : !R.isNil(planet)

      if (!pass) {
        throw new CustomError(
          'wait for operation result',
          ERROR_CODE.CONTRACT.WAIT_SEND_OPERATION_RESULT
        )
      }

      return true
    },
    [query]
  )

  const verify = useCallback(
    async (
      planetID: string,
      passCondition?: (planet: Planet) => boolean
    ): Promise<boolean> => {
      try {
        await pRetry(async () => {
          await doVerify(planetID, passCondition)
        }, RETRY.GRAPHQL.POLL_AFTER_TX_SUCCESS.DEFAULT)

        return true
      } catch (error) {
        return false
      }
    },
    [doVerify]
  )

  return { verify }
}

type Hook = {
  verify: (
    planetID: string,
    passCondition?: (planet: Planet) => boolean
  ) => Promise<boolean>
}

export default useVerifyPlanet
